import { v4 as uuidv4 } from 'uuid';
import { LOCAL_ASSET } from './localAssets.js';
import {
	createPayFileUnlockPreviewItemsFromPages,
	getPayFileUnlockDefaults,
	getPayFileUnlockFileIconKey,
	getPayFileUnlockFileIconMark,
	getPayFileUnlockGalleryUrls,
	getPayFileUnlockPreviewImageUrl,
	isPayFileUnlockBlock,
	normalizePayFileUnlockData,
} from './payFileUnlock.js';

export const PAY_FILE_LIST_TYPE = 'pay-file-list';
export const PAY_FILE_LIST_GRID_COL_COUNT = 3;
export const PAY_FILE_LIST_COMPACT_PREVIEW_ROW_COUNT = 2;
export const PAY_FILE_LIST_COMPACT_FREE_COUNT = 1;

export const DEFAULT_PAY_FILE_HEADER_FEATURES = [
	{ id: uuidv4(), text: '安全加密', icon: 'shield' },
	{ id: uuidv4(), text: '支持下载', icon: 'download' },
	{ id: uuidv4(), text: '永久有效', icon: 'valid' },
];

function getFixedHeaderFeatures() {
	return DEFAULT_PAY_FILE_HEADER_FEATURES.map(item => ({ ...item, id: uuidv4() }));
}

function getFileTypeLabel(fileName = '') {
	const ext = (fileName.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'PDF 文档';
	if (['doc', 'docx'].includes(ext)) return 'Word 文档';
	if (['xls', 'xlsx'].includes(ext)) return 'Excel 表格';
	if (['ppt', 'pptx'].includes(ext)) return 'PPT 文档';
	return '文档';
}

function createPayFileListDemoPreviewItem(index = 0) {
	const localImgs = LOCAL_ASSET.payFileList;
	return {
		id: uuidv4(),
		image: {
			id: uuidv4(),
			filename: `pay-file-list-${index + 1}.png`,
			previewUrl: localImgs[index % localImgs.length],
			url: '',
			local: false,
		},
	};
}

export function createPayFileListDemoPreviewItems(count = 6) {
	return Array.from({ length: count }, (_, index) => createPayFileListDemoPreviewItem(index));
}

export function normalizePayFileListData(data = {}) {
	const base = normalizePayFileUnlockData(data);
	return {
		...base,
		headerFeatures: getFixedHeaderFeatures(),
	};
}

export function getPayFileListDefaults(extra = {}) {
	return normalizePayFileListData(getPayFileUnlockDefaults(extra));
}

export function getPayFileListPreviewDefaults() {
	return normalizePayFileListData({
		fileName: '虚拟资源项目执行方案.docx',
		file: {
			filesize: '2.48MB',
		},
		items: createPayFileListDemoPreviewItems(6),
		freePreviewCount: 1,
	});
}

export function isPayFileListBlock(type) {
	return type === PAY_FILE_LIST_TYPE;
}

export function isPayFileEditBlock(type) {
	return isPayFileUnlockBlock(type) || isPayFileListBlock(type);
}

export {
	createPayFileUnlockPreviewItemsFromPages,
	getPayFileUnlockFileIconKey,
	getPayFileUnlockFileIconMark,
	getPayFileUnlockGalleryUrls,
	getPayFileUnlockPreviewImageUrl,
};

export function getPayFileListMetaText(data = {}) {
	const normalized = normalizePayFileListData(data);
	const pageCount = normalized.items.length || 0;
	const fileSize = normalized.file?.filesize || '';
	const parts = [getFileTypeLabel(normalized.fileName)];
	if (pageCount > 0) {
		parts.push(`${pageCount}页`);
	}
	if (fileSize) {
		parts.push(fileSize);
	}
	return parts.join(' · ');
}

export function getPayFileListPreviewRows(data = {}, { unlocked = false, compact = false } = {}) {
	const normalized = normalizePayFileListData(data);
	const pageCount = normalized.items.length;
	const freeCount = compact
		? Math.min(PAY_FILE_LIST_COMPACT_FREE_COUNT, normalized.freePreviewCount, pageCount)
		: Math.min(normalized.freePreviewCount, pageCount);

	if (unlocked) {
		const items = compact
			? normalized.items.slice(0, PAY_FILE_LIST_COMPACT_PREVIEW_ROW_COUNT)
			: normalized.items;
		return items.map((item, index) => ({
			key: item.id,
			index,
			image: item.image,
			locked: false,
		}));
	}

	const rows = [];
	normalized.items.slice(0, freeCount).forEach((item, index) => {
		rows.push({
			key: item.id,
			index,
			image: item.image,
			locked: false,
		});
	});

	if (pageCount > freeCount) {
		const startPage = freeCount + 1;
		const endPage = pageCount;
		rows.push({
			key: 'locked-summary',
			index: freeCount,
			image: normalized.items[freeCount]?.image
				|| normalized.items[freeCount - 1]?.image
				|| null,
			locked: true,
		});
	}

	if (compact) {
		return rows.slice(0, PAY_FILE_LIST_COMPACT_PREVIEW_ROW_COUNT);
	}

	return rows;
}

export function getPayFileListUnlockKey(blockId) {
	return blockId ? `gedit-pay-file-list-${blockId}` : '';
}

export function isPayFileListUnlocked(blockId) {
	const key = getPayFileListUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return false;
	return sessionStorage.getItem(key) === '1';
}

export function setPayFileListUnlocked(blockId, unlocked = true) {
	const key = getPayFileListUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return;
	if (unlocked) {
		sessionStorage.setItem(key, '1');
	} else {
		sessionStorage.removeItem(key);
	}
}
